import clsx from "clsx";
import type { SVGAttributes } from "react";

/*
  The drawn icons of the app (rebuild decision D2): small line icons on a 24 x 24 grid, drawn here, so nothing is loaded from
  the internet and no icon package is needed. Each icon is a list of path strings; the stroke is the text colour.
*/

const CIRCLE = (cx: number, cy: number, r: number) => `M${cx} ${cy}m-${r} 0a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 -${r * 2} 0`;

const PATHS = {
  help: [CIRCLE(12, 12, 9), "M12 17v.01", "M12 13.5a1.5 1.5 0 0 1 1-1.5a2.6 2.6 0 1 0-3-4"],
  search: [CIRCLE(10, 10, 7), "M21 21l-6-6"],
  "chevron-down": ["M6 9l6 6l6-6"],
  "chevron-up": ["M6 15l6-6l6 6"],
  "chevron-left": ["M15 6l-6 6l6 6"],
  "chevron-right": ["M9 6l6 6l-6 6"],
  plus: ["M12 5v14", "M5 12h14"],
  minus: ["M5 12h14"],
  x: ["M18 6l-12 12", "M6 6l12 12"],
  check: ["M5 12l5 5l10-10"],
  "check-circle": [CIRCLE(12, 12, 9), "M9 12l2 2l4-4"],
  "panel-left-close": ["M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2z", "M9 4v16", "M15 10l-2 2l2 2"],
  "panel-left-open": ["M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2z", "M9 4v16", "M13 10l2 2l-2 2"],
  "panel-right-close": ["M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2z", "M15 4v16", "M9 10l2 2l-2 2"],
  "panel-right-open": ["M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2z", "M15 4v16", "M11 10l-2 2l2 2"],
  "zoom-in": [CIRCLE(10, 10, 7), "M7 10h6", "M10 7v6", "M21 21l-6-6"],
  "zoom-out": [CIRCLE(10, 10, 7), "M7 10h6", "M21 21l-6-6"],
  focus: [CIRCLE(12, 12, 3), "M4 8v-2a2 2 0 0 1 2-2h2", "M4 16v2a2 2 0 0 0 2 2h2", "M16 4h2a2 2 0 0 1 2 2v2", "M16 20h2a2 2 0 0 0 2-2v-2"],
  paperclip: ["M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5-6.5a3 3 0 0 0-6-6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5-6.5"],
  send: ["M10 14l11-11", "M21 3l-6.5 18a.55.55 0 0 1-1 0l-3.5-7l-7-3.5a.55.55 0 0 1 0-1l18-6.5"],
  graph: [CIRCLE(12, 12, 2.5), CIRCLE(5, 6, 2), CIRCLE(19, 6, 2), CIRCLE(12, 20, 2), "M6.5 7.5l3.5 3", "M17.5 7.5l-3.5 3", "M12 14.5v3.5"],
  table: ["M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-14a2 2 0 0 1-2-2z", "M3 10h18", "M10 3v18"],
  alert: ["M12 9v4", "M10.36 3.59l-8.1 13.53a1.91 1.91 0 0 0 1.64 2.87h16.2a1.91 1.91 0 0 0 1.64-2.87l-8.1-13.53a1.91 1.91 0 0 0-3.28 0z", "M12 16h.01"],
  info: [CIRCLE(12, 12, 9), "M12 9h.01", "M11 12h1v4h1"],
  lock: ["M5 13a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2z", CIRCLE(12, 16, 1), "M8 11v-4a4 4 0 1 1 8 0v4"],
  file: ["M14 3v4a1 1 0 0 0 1 1h4", "M17 21h-10a2 2 0 0 1-2-2v-14a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"],
  upload: ["M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2", "M7 9l5-5l5 5", "M12 4v12"],
  pencil: ["M4 20h4l10.5-10.5a2.83 2.83 0 1 0-4-4l-10.5 10.5v4", "M13.5 6.5l4 4"],
  trash: ["M4 7h16", "M10 11v6", "M14 11v6", "M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12", "M9 7v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"],
  link: ["M9 15l6-6", "M11 6l.46-.54a5 5 0 0 1 7.07 7.08l-.53.46", "M13 18l-.4.53a5.07 5.07 0 0 1-7.13 0a4.97 4.97 0 0 1 0-7.07l.52-.46"],
  bell: ["M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6", "M9 17v1a3 3 0 0 0 6 0v-1"],
  user: [CIRCLE(12, 7, 4), "M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"],
  sliders: ["M4 6h8", "M16 6h4", "M4 12h2", "M10 12h10", "M4 18h10", "M18 18h2", CIRCLE(14, 6, 2), CIRCLE(8, 12, 2), CIRCLE(16, 18, 2)],
  dots: [CIRCLE(5, 12, 1), CIRCLE(12, 12, 1), CIRCLE(19, 12, 1)],
  refresh: ["M20 11a8.1 8.1 0 0 0-15.5-2m-.5-4v4h4", "M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"],
  sparkle: ["M12 3c.5 4.5 2.5 7 8 9c-5.5 2-7.5 4.5-8 9c-.5-4.5-2.5-7-8-9c5.5-2 7.5-4.5 8-9z"],
  "arrow-right": ["M5 12h14", "M13 18l6-6", "M13 6l6 6"],
  "external-link": ["M12 6h-6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6", "M11 13l9-9", "M15 4h5v5"],
  message: ["M8 9h8", "M8 13h6", "M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 3v-3h-2a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3z"],
} as const satisfies Record<string, readonly string[]>;

/** The name of an icon. A name that is not drawn is a type error, so a screen cannot ask for an icon that is missing. */
export type IconName = keyof typeof PATHS;

/** Every drawn icon, for a test page that shows them all. */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export interface IconProps extends Omit<SVGAttributes<SVGSVGElement>, "children"> {
  name: IconName;
  /** Width and height in pixels. 14 is the size of the sketches. */
  size?: number;
  /**
   * Says what the icon means, for the rare icon that stands alone. Left out, the icon is decoration and is hidden from screen
   * readers: the words next to it, or the label of the button that holds it, carry the meaning.
   */
  label?: string;
}

/** A drawn line icon in the colour of the text around it. */
export function Icon({ name, size = 14, label, className, ...rest }: IconProps) {
  return (
    <svg
      className={clsx("svg-icon", className)}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      data-icon={name}
      {...rest}
    >
      {PATHS[name].map((d, index) => (
        <path key={index} d={d} />
      ))}
    </svg>
  );
}
